import React, { useEffect, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useIsCompanyGoalsManagePage } from 'Deer/intent/Goals/components/MetaFilters';
import {
  setSelectedLocations,
  setSelectedCompanies,
  useAllGoalsLocations,
  useAllGoalsCompanies,
  useSelectedGoalsLocations,
  useSelectedGoalsCompanies,
} from 'Deer/intent/Goals/slice/GoalsIntentSlice';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { useDispatch } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';
import { areArraysEqual } from 'utils/helpers/utilsHelper';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';

import { GoalsObjectiveGenericTreeViewTable } from './GoalsObjectiveGenericTreeViewTable';
import { goalsCascadedViewReactQueryKey, useGoalsCascadedViewData } from './hooks/dataHooks';
import {
  useSetCompanyMetaFilterInitialDataGoals,
  useSetLocationMetaFilterInitialDataGoals,
} from './hooks/metaFiltersHooks';
import './styles/TreeView.css';

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

export const usePersistenceKey = ({ showParentObjectives = false, viewId = '' } = {}) => {
  const sideNavSelectedCohort = useSelectedView();
  const viewId1 = viewId !== '' ? viewId : sideNavSelectedCohort?.id;
  /* Grid State persistence Key */
  const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
  const tabId = 'Explore';
  const uniqueId = showParentObjectives ? 'ExploreGrid' : 'ListGrid';

  return getPersistenceKey({
    moduleName,
    viewId: viewId1,
    tabId,
    uniqueId,
  });
};

const GoalsTreeViewTable = ({
  attributeFilter,
  timeBoundPeriod,
  isHierarchyView,
  gridRef,
  groupBy = GROUP_BY,
  pageType = 'TEAM_HUB',
  hasImplicitDepartmentFilter = false,
  setChartData = () => {},
  showParentObjectives = false,
  setRowData = () => {},
  onModalUpdated = () => {},
  ownership,
}) => {
  const sideNavSelectedCohort = useSelectedView();
  const gridApi = gridRef?.current?.api;
  const selectedCompanies = useSelectedGoalsCompanies();
  const allCompanies = useAllGoalsCompanies();
  const selectedLocations = useSelectedGoalsLocations();
  const allLocations = useAllGoalsLocations();
  const selectedCohortId = sideNavSelectedCohort?.id;
  const dispatch = useDispatch();
  const { data, isLoading } = useGoalsCascadedViewData({
    attributeFilter,
    cohort: sideNavSelectedCohort,
    groupByKeys: groupBy,
    splitByKeys: SPLIT_BY,
    orderBy: null,
    timeBounds: [timeBoundPeriod],
    isHierarchyView,
    pageType,
    mergeColumns: selectedCohortId !== -12,
    ownership,
  });

  useSetCompanyMetaFilterInitialDataGoals(data);
  useSetLocationMetaFilterInitialDataGoals(data);

  const queryClient = useQueryClient();

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries(
      goalsCascadedViewReactQueryKey({
        cohort: sideNavSelectedCohort,
        groupByKeys: groupBy,
        splitByKeys: SPLIT_BY,
        orderBy: null,
        timeBounds: [timeBoundPeriod],
        attributeFilter,
        isHierarchyView,
        pageType,
      })
    );
  };

  const gridStatePersistenceKey = usePersistenceKey({ showParentObjectives });

  useEffect(() => {
    setChartData((prev) => ({ ...prev, isLoading, rowData: data }));
    if (gridApi) {
      if (isLoading) {
        gridApi.setGridOption('loading', true);
      } else if (data && data.length === 0) {
        gridApi.setGridOption('loading', false);
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.setGridOption('loading', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, gridApi]);

  const isCompanyGoalsManagePage = useIsCompanyGoalsManagePage();

  const rowsData = useMemo(() => {
    const rowsDataBeforeFilters = data || [];

    if (checkEnableCompanySupport() && isCompanyGoalsManagePage) {
      if (gridApi) {
        gridApi.setColumnFilterModel('companies', {
          filterType: 'multi',
          filterModels: [
            {
              filterType: 'set',
              values: selectedCompanies.map((s) => s.companyName),
            },
            null,
          ],
        });

        gridApi.setColumnFilterModel('empLocations', {
          filterType: 'multi',
          filterModels: [
            {
              filterType: 'set',
              values: selectedLocations.map((empLocation) => empLocation),
            },
            null,
          ],
        });

        gridApi.onFilterChanged(); // Refresh grid filtering
      }
    }

    return rowsDataBeforeFilters;
  }, [data, gridApi, isCompanyGoalsManagePage, selectedLocations, selectedCompanies]);

  useEffect(() => {
    const handleFilterChanged = () => {
      const filterModel = gridApi.getFilterModel();

      // some locations are unselected in ag-grid, update meta filter values if mismatched
      if (filterModel?.empLocations) {
        const filteredEmpLocations = filterModel?.empLocations?.filterModels?.[0]?.values || [];

        if (!areArraysEqual(filteredEmpLocations, selectedLocations)) {
          dispatch(setSelectedLocations(filteredEmpLocations));
        }
      } else if (filterModel?.companies) {
        const filteredCompanies = filterModel?.companies?.filterModels?.[0]?.values || [];

        if (
          !areArraysEqual(
            filteredCompanies,
            selectedCompanies.map((s) => s.companyName)
          )
        ) {
          dispatch(
            setSelectedCompanies(
              filteredCompanies.map((companyName) => {
                const company = allCompanies.find((s) => s.companyName === companyName);
                return company;
              })
            )
          );
        }
      }
      // all companies selected in ag-grid, update meta filter values
      else if (allCompanies.length !== selectedCompanies.length) {
        dispatch(setSelectedCompanies(allCompanies));
      }

      // all locations selected in ag-grid, update meta filter values
      else if (allLocations.length !== selectedLocations.length) {
        dispatch(setSelectedLocations(allLocations));
      }
    };

    if (gridApi) {
      gridApi.addEventListener('filterChanged', handleFilterChanged);
    }

    return () => {
      if (gridApi) {
        gridApi.removeEventListener('filterChanged', handleFilterChanged);
      }
    };
  }, [gridApi, allLocations, dispatch, selectedLocations, allCompanies, selectedCompanies]);

  useEffect(() => {
    setRowData(rowsData);
  }, [rowsData, setRowData]);

  return (
    <GoalsObjectiveGenericTreeViewTable
      gridRef={gridRef}
      key={gridStatePersistenceKey}
      gridStatePersistenceKey={gridStatePersistenceKey}
      data={rowsData}
      refetchHandler={refetchCascadedViewData}
      hasImplicitDepartmentFilter={hasImplicitDepartmentFilter}
      setChartData={setChartData}
      onModalUpdated={onModalUpdated}
      source="Goals"
    />
  );
};

export { GoalsTreeViewTable };
